import React from 'react';
import { Link } from 'react-router-dom';
import './PersonalVaultPrivacyPage.css';

const PersonalVaultPrivacyPage: React.FC = () => {
  return (
    <div className="privacy-page">
      <div className="privacy-container">
        <section className="privacy-hero">
          <div className="privacy-hero-row">
            <div>
              <h1>Privacy Policy</h1>
              <div className="privacy-meta">Personal Vault: Item Tracker — Privacy Policy</div>
            </div>
            <div className="privacy-hero-right">
              <div className="privacy-badge">Version 1.0</div>
              <div className="privacy-updated">Last Updated: March 23, 2026</div>
            </div>
          </div>
        </section>

        <article className="privacy-card">
          <h2>Privacy Policy</h2>
          <p>Personal Vault: Item Tracker is built on a single principle: <strong>your data belongs to you.</strong></p>
          <p>
            Please also read our{' '}
            <Link to="/personal-vault/terms">Terms of Service</Link>, which govern your use of the App.
          </p>

          <h2>1. Local Data Storage</h2>
          <p>User data is stored locally on your device. We do not upload, transmit, or store your data on our servers.</p>
          <p>Data you store in Personal Vault — collections, groups, tags, images, notes, and prices — is stored on your device. We do not maintain servers or accounts for receiving your data.</p>
          <ul>
            <li><strong>No accounts required</strong>: The app operates without requiring registration or login</li>
            <li><strong>Local storage</strong>: When you close the app, your data remains on your device</li>
            <li><strong>No server backup</strong>: We do not maintain servers for data backup. Future updates may support cloud backup services that you control (such as Google Drive, iCloud, Dropbox, etc.)</li>
          </ul>
          <p>Because the App does not collect or transmit your in-app content, we cannot recover lost data or access encrypted content. See our <Link to="/personal-vault/terms">Terms of Service</Link> for user responsibilities regarding data backup and passphrase management.</p>

          <h2>2. Encryption and Security</h2>
          <p>If you choose to enable encryption, your data is protected by <strong>industry-standard AES-256 encryption</strong> derived from a passphrase that only you know.</p>
          <ul>
            <li>The passphrase is stored in the <strong>secure enclave</strong> of your device</li>
            <li>The passphrase is <strong>never transmitted</strong> — not even to us (because we have no servers)</li>
            <li>Without your passphrase, your encrypted data cannot be accessed by anyone, including us</li>
          </ul>

          <h2>3. Export and Import of Data</h2>
          <p>The App may allow users to export or import encrypted data files. When you export data, the resulting file goes <strong>directly to a destination you select</strong> — such as local storage or a cloud drive you control. We do not receive, access, or store exported files.</p>

          <h2>4. Advertising and Third-Party Services (AdMob)</h2>
          <p>The App uses Google AdMob to display advertisements. While we do not collect or transmit any personal or app-stored data, AdMob may independently collect certain device or usage information for the purpose of delivering, measuring, and improving ads.</p>
          <p>AdMob may collect information such as device identifiers, approximate location, IP address, and ad interaction data. This data collection is governed solely by <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Google&apos;s Privacy Policy</a> and is not used by us for any purpose.</p>
          <p><strong>We do not share any data stored in Personal Vault with AdMob or any third party.</strong> All your in-app content — including collections, tags, notes, images, and encrypted data — remains on your device and is not accessible to the advertising provider.</p>
          <p>Users may opt out of personalized ads using the device&apos;s system settings or <a href="https://adssettings.google.com" target="_blank" rel="noopener noreferrer">Google ad preferences</a>. Disabling personalized ads does not affect app functionality.</p>
          <p>If you have concerns about AdMob&apos;s data practices, please refer to <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Google&apos;s Privacy Policy</a> for more details.</p>

          <h2>5. Device Permissions</h2>
          <p>Personal Vault may request the following permissions:</p>
          <ul>
            <li><strong>Storage/Photos</strong>: To save and access images you add to your collections (stored locally only)</li>
            <li><strong>File System</strong>: To export your data to a location you choose</li>
            <li><strong>Biometric Authentication</strong>: To provide secure access to your encrypted collections (optional)</li>
          </ul>
          <p>These permissions are used <strong>exclusively for local operations</strong> — no data is transmitted.</p>

          <h2>6. Changes to This Policy</h2>
          <p>If we make changes to this Privacy Policy, we will update the &quot;Last Updated&quot; date. However, our core principle remains unchanged: <strong>your data belongs to you, and we will never collect it.</strong></p>

          <h2>Summary</h2>
          <p>We do not collect or transmit your in-app content. Your collections, notes, and stored data remain on your device. AdMob may collect device-level data for advertising purposes; we do not share any of your app data with AdMob or any third party.</p>

          <footer className="privacy-footer">
            <div>© Personal Vault: Item Tracker — All rights reserved.</div>
          </footer>
        </article>

        <div className="privacy-back">
          <Link to="/personal-vault" className="privacy-back-link">← Back to Personal Vault: Item Tracker</Link>
        </div>
      </div>
    </div>
  );
};

export default PersonalVaultPrivacyPage;
